import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { 
  Observable, 
  BehaviorSubject, 
  of, 
  throwError, 
  timer,
  Subject,
  shareReplay,
  catchError,
  tap,
  finalize,
  switchMap,
  filter,
  take,
  map
} from 'rxjs';
import { ApiService, ApiResponse } from './api.service';

// Cache entry interface
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
  isRefreshing?: boolean;
}

// Request deduplication interface
interface PendingRequest<T> {
  request: Observable<T>;
  subscribers: Subject<T>[];
}

// Loading state interface
interface LoadingState {
  [key: string]: boolean;
}

// API call configuration
interface ApiCallConfig {
  cacheKey?: string;
  ttl?: number; // Time to live in milliseconds
  deduplicate?: boolean;
  retryAttempts?: number;
  retryDelay?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ApiOptimizationService {
  private readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes
  private readonly DEFAULT_RETRY_ATTEMPTS = 3;
  private readonly DEFAULT_RETRY_DELAY = 1000; // 1 second

  // Cache storage
  private cache = new Map<string, CacheEntry<any>>();
  
  // Request deduplication
  private pendingRequests = new Map<string, PendingRequest<any>>();
  
  // Loading states
  private loadingStates = new BehaviorSubject<LoadingState>({});
  
  // Global loading state
  private globalLoading = new BehaviorSubject<boolean>(false);
  
  // Cache statistics
  private cacheStats = {
    hits: 0,
    misses: 0,
    evictions: 0
  };

  constructor(
    private http: HttpClient,
    private apiService: ApiService
  ) {
    // Clean up expired cache entries periodically
    this.startCacheCleanup();
  }

  // ==================== PUBLIC METHODS ====================

  /**
   * Optimized GET request with caching and deduplication
   */
  get<T>(url: string, params?: any, config: ApiCallConfig = {}): Observable<T> {
    const cacheKey = this.generateCacheKey('GET', url, params);
    const ttl = config.ttl || this.DEFAULT_TTL;
    const shouldDeduplicate = config.deduplicate !== false;

    // Check cache first
    const cachedData = this.getFromCache<T>(cacheKey);
    if (cachedData) {
      this.cacheStats.hits++;
      return of(cachedData);
    }

    this.cacheStats.misses++;

    // Check for pending request
    if (shouldDeduplicate && this.pendingRequests.has(cacheKey)) {
      const pending = this.pendingRequests.get(cacheKey)!;
      const subject = new Subject<T>();
      pending.subscribers.push(subject);
      return subject.asObservable();
    }

    // Set loading state
    this.setLoadingState(cacheKey, true);

    // Create new request
    const request = this.http.get<T>(url, { params: this.buildHttpParams(params) })
      .pipe(
        tap(response => {
          // Cache the response
          this.setCache(cacheKey, response, ttl);
          
          // Notify subscribers
          if (shouldDeduplicate && this.pendingRequests.has(cacheKey)) {
            const pending = this.pendingRequests.get(cacheKey)!;
            pending.subscribers.forEach(subject => subject.next(response));
            pending.subscribers.forEach(subject => subject.complete());
            this.pendingRequests.delete(cacheKey);
          }
        }),
        catchError(error => {
          // Remove from pending requests on error
          if (shouldDeduplicate) {
            this.pendingRequests.delete(cacheKey);
          }
          return throwError(() => error);
        }),
        finalize(() => {
          this.setLoadingState(cacheKey, false);
        }),
        shareReplay(1)
      );

    // Store pending request for deduplication
    if (shouldDeduplicate) {
      this.pendingRequests.set(cacheKey, {
        request,
        subscribers: []
      });
    }

    return request;
  }

  /**
   * Optimized POST request with caching and deduplication
   */
  post<T>(url: string, body: any, config: ApiCallConfig = {}): Observable<T> {
    const cacheKey = this.generateCacheKey('POST', url, body);
    const shouldDeduplicate = config.deduplicate !== false;

    // Check for pending request
    if (shouldDeduplicate && this.pendingRequests.has(cacheKey)) {
      const pending = this.pendingRequests.get(cacheKey)!;
      const subject = new Subject<T>();
      pending.subscribers.push(subject);
      return subject.asObservable();
    }

    // Set loading state
    this.setLoadingState(cacheKey, true);

    // Create new request
    const request = this.http.post<T>(url, body)
      .pipe(
        tap(response => {
          // Notify subscribers
          if (shouldDeduplicate && this.pendingRequests.has(cacheKey)) {
            const pending = this.pendingRequests.get(cacheKey)!;
            pending.subscribers.forEach(subject => subject.next(response));
            pending.subscribers.forEach(subject => subject.complete());
            this.pendingRequests.delete(cacheKey);
          }
        }),
        catchError(error => {
          // Remove from pending requests on error
          if (shouldDeduplicate) {
            this.pendingRequests.delete(cacheKey);
          }
          return throwError(() => error);
        }),
        finalize(() => {
          this.setLoadingState(cacheKey, false);
        }),
        shareReplay(1)
      );

    // Store pending request for deduplication
    if (shouldDeduplicate) {
      this.pendingRequests.set(cacheKey, {
        request,
        subscribers: []
      });
    }

    return request;
  }

  /**
   * Optimized PUT request
   */
  put<T>(url: string, body: any, config: ApiCallConfig = {}): Observable<T> {
    return this.http.put<T>(url, body)
      .pipe(
        tap(() => this.invalidateRelatedCache(url)),
        this.handleErrorWithRetry(config)
      );
  }

  /**
   * Optimized PATCH request
   */
  patch<T>(url: string, body: any, config: ApiCallConfig = {}): Observable<T> {
    return this.http.patch<T>(url, body)
      .pipe(
        tap(() => this.invalidateRelatedCache(url)),
        this.handleErrorWithRetry(config)
      );
  }

  /**
   * Optimized DELETE request
   */
  delete<T>(url: string, config: ApiCallConfig = {}): Observable<T> {
    return this.http.delete<T>(url)
      .pipe(
        tap(() => this.invalidateRelatedCache(url)),
        this.handleErrorWithRetry(config)
      );
  }

  // ==================== CACHE MANAGEMENT ====================

  /**
   * Get data from cache
   */
  private getFromCache<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    const now = Date.now();
    if (now - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      this.cacheStats.evictions++;
      return null;
    }

    return entry.data;
  }

  /**
   * Set data in cache
   */
  private setCache<T>(key: string, data: T, ttl: number): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }

  /**
   * Invalidate cache entries related to a URL
   */
  private invalidateRelatedCache(url: string): void {
    const urlParts = url.split('/');
    const basePath = urlParts.slice(0, -1).join('/');
    
    for (const [key] of this.cache) {
      if (key.includes(basePath)) {
        this.cache.delete(key);
        this.cacheStats.evictions++;
      }
    }
  }

  /**
   * Clear all cache
   */
  clearCache(): void {
    this.cache.clear();
    this.cacheStats.evictions += this.cache.size;
  }

  /**
   * Clear cache by pattern
   */
  clearCacheByPattern(pattern: string): void {
    for (const [key] of this.cache) {
      if (key.includes(pattern)) {
        this.cache.delete(key);
        this.cacheStats.evictions++;
      }
    }
  }

  // ==================== LOADING STATE MANAGEMENT ====================

  /**
   * Set loading state for a specific key
   */
  private setLoadingState(key: string, loading: boolean): void {
    const currentStates = this.loadingStates.value;
    if (loading) {
      currentStates[key] = true;
      this.globalLoading.next(true);
    } else {
      delete currentStates[key];
      // Check if any other requests are still loading
      const hasAnyLoading = Object.keys(currentStates).length > 0;
      this.globalLoading.next(hasAnyLoading);
    }
    this.loadingStates.next(currentStates);
  }

  /**
   * Get loading state for a specific key
   */
  getLoadingState(key: string): Observable<boolean> {
    return this.loadingStates.pipe(
      map(states => states[key] || false)
    );
  }

  /**
   * Get global loading state
   */
  getGlobalLoadingState(): Observable<boolean> {
    return this.globalLoading.asObservable();
  }

  // ==================== UTILITY METHODS ====================

  /**
   * Generate cache key from request details
   */
  private generateCacheKey(method: string, url: string, params?: any): string {
    const paramsString = params ? JSON.stringify(params) : '';
    return `${method}:${url}:${paramsString}`;
  }

  /**
   * Build HTTP params
   */
  private buildHttpParams(params: any): HttpParams {
    if (!params) return new HttpParams();
    
    let httpParams = new HttpParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined) {
        httpParams = httpParams.set(key, params[key].toString());
      }
    });
    return httpParams;
  }

  /**
   * Handle errors with retry logic
   */
  private handleErrorWithRetry<T>(config: ApiCallConfig) {
    const retryAttempts = config.retryAttempts || this.DEFAULT_RETRY_ATTEMPTS;
    const retryDelay = config.retryDelay || this.DEFAULT_RETRY_DELAY;

    return (source: Observable<T>) => source.pipe(
      catchError((error, caught) => {
        if (retryAttempts > 0) {
          return timer(retryDelay).pipe(
            switchMap(() => caught)
          );
        }
        return throwError(() => error);
      })
    );
  }

  /**
   * Start periodic cache cleanup
   */
  private startCacheCleanup(): void {
    // Clean up expired entries every 5 minutes, starting after a 5-second delay
    timer(5000, 5 * 60 * 1000).subscribe(() => {
      const now = Date.now();
      for (const [key, entry] of this.cache) {
        if (now - entry.timestamp > entry.ttl) {
          this.cache.delete(key);
          this.cacheStats.evictions++;
        }
      }
    });
  }

  // ==================== STATISTICS ====================

  /**
   * Get cache statistics
   */
  getCacheStats() {
    return {
      ...this.cacheStats,
      size: this.cache.size,
      hitRate: this.cacheStats.hits / (this.cacheStats.hits + this.cacheStats.misses)
    };
  }

  /**
   * Get cache size
   */
  getCacheSize(): number {
    return this.cache.size;
  }

  // ==================== SPECIFIC API METHODS ====================

  /**
   * Optimized method to get organizations with caching
   */
  getOrganizations(params?: any): Observable<ApiResponse> {
    return this.get<ApiResponse>(
      `${this.apiService['baseUrl']}/organization`,
      params,
      {
        cacheKey: 'organizations',
        ttl: 10 * 60 * 1000, // 10 minutes
        deduplicate: true
      }
    );
  }

  /**
   * Optimized method to get parkings with caching
   */
  getParkings(params?: any): Observable<ApiResponse> {
    return this.get<ApiResponse>(
      `${this.apiService['baseUrl']}/parkings`,
      params,
      {
        cacheKey: 'parkings',
        ttl: 5 * 60 * 1000, // 5 minutes
        deduplicate: true
      }
    );
  }

  /**
   * Optimized method to get teams with caching
   * Uses new API endpoint: /organization/{organizationId}/user/all
   */
  getTeams(orgId?: string): Observable<any[]> {
    const url = `${this.apiService['baseUrl']}/organization/${orgId}/user/all`;
    return this.get<any[]>(
      url,
      undefined,
      {
        cacheKey: `teams_${orgId}`,
        ttl: 15 * 60 * 1000, // 15 minutes
        deduplicate: true
      }
    );
  }

  /**
   * Optimized method to get parking passes with caching
   */
  getParkingPasses(parkingId: string, params?: any): Observable<ApiResponse> {
    const url = `${this.apiService['baseUrl']}/parking/${parkingId}/passes`;
    return this.get<ApiResponse>(
      url,
      params,
      {
        cacheKey: `passes_${parkingId}`,
        ttl: 2 * 60 * 1000, // 2 minutes
        deduplicate: true
      }
    );
  }
}
